import React, { useState, useEffect } from "react";
import { Button, Form } from "react-bootstrap";
import { useSelector } from "react-redux";
import Select from "react-select";
import { MdOutlineDeleteOutline } from "react-icons/md";

const MapVisualization = ({
    indicators,
    locations,
    onSubmit,
    localSelectedLocations,
    setLocalSelectedLocations,
    chartType,
    setChartType,
    colorIndicator,
    setColorIndicator,
    colorSubIndicators,
    setColorSubIndicators,
    selectedColorSubIndicator,
    setSelectedColorSubIndicator,
    patternIndicator,
    setPatternIndicator,
    patternSubIndicators,
    setPatternSubIndicators,
    selectedPatternSubIndicator,
    setSelectedPatternSubIndicator,
    typeIndicators,
    setTypeIndicators,
    typeSubIndicators,
    setTypeSubIndicators,
    selectedTypeSubIndicators,
    setSelectedTypeSubIndicators
}) => {
    // ✅ Get Redux state for restoration
    const reduxMapConfig = useSelector(state => state.mapConfig);

    // Local state only for UI control
    const [isBool, setBool] = useState(true);
    const [isMapSubmitDisabled, setIsMapSubmitDisabled] = useState(true);
    const [isRestored, setIsRestored] = useState(false);

    // Helper function for geo level labels
    const geoLevelLabel = (level) => {
        switch (level) {
            case 1: return "National";
            case 2: return "State / UT";
            case 3: return "District";
            case 4: return "ULG";
            default: return "Unknown";
        }
    };

    const noOptionsMessage = () => "No compatible indicators found in your workspace.";

    // ✅ Restore from Redux on component mount
    useEffect(() => {
        if (!reduxMapConfig || isRestored) return;
        if (!reduxMapConfig.chartType) return; // Nothing to restore

        // Restore chart type
        setChartType(reduxMapConfig.chartType);

        // Restore color indicator
        if (reduxMapConfig.colorIndicator) {
            const colorInd = indicators.find(f => f.indicator_code === reduxMapConfig.colorIndicator);
            if (colorInd) {
                setColorIndicator({
                    value: reduxMapConfig.colorIndicator,
                    label: `${colorInd.indicator_name} (${colorInd.indicator_date})`,
                });
                setColorSubIndicators(colorInd.sub_indicators || []);

                // Restore color sub-indicator
                if (reduxMapConfig.colorSubIndicator) {
                    const colorSubInd = colorInd.sub_indicators?.find(
                        s => s.code === reduxMapConfig.colorSubIndicator
                    );
                    if (colorSubInd) {
                        setSelectedColorSubIndicator({
                            value: colorSubInd.code,
                            label: colorSubInd.name
                        });
                    }
                }
            }
        }

        // Restore pattern indicator
        if (reduxMapConfig.patternIndicator) {
            const patternInd = indicators.find(f => f.indicator_code === reduxMapConfig.patternIndicator);
            if (patternInd) {
                setPatternIndicator({
                    value: reduxMapConfig.patternIndicator,
                    label: `${patternInd.indicator_name} (${patternInd.indicator_date})`,
                });
                setPatternSubIndicators(patternInd.sub_indicators || []);

                // Restore pattern sub-indicator
                if (reduxMapConfig.patternSubIndicator) {
                    const patternSubInd = patternInd.sub_indicators?.find(
                        s => s.code === reduxMapConfig.patternSubIndicator
                    );
                    if (patternSubInd) {
                        setSelectedPatternSubIndicator({
                            value: patternSubInd.code,
                            label: patternSubInd.name
                        });
                    }
                }
            }
        }

        // Restore type indicators (bubble/marker/pie)
        if (reduxMapConfig.indicators?.length > 0) {
            const restoredTypeIndicators = [];
            const restoredTypeSubIndicators = {};
            const restoredSelectedSubs = {};

            reduxMapConfig.indicators.forEach((savedInd, idx) => {
                if (!savedInd.indicator_code) return;

                const foundInd = indicators.find(f => f.indicator_code === savedInd.indicator_code);
                if (foundInd) {
                    restoredTypeIndicators.push({
                        value: foundInd.indicator_code,
                        label: `${foundInd.indicator_name} (${foundInd.indicator_date})`
                    });

                    restoredTypeSubIndicators[idx] = foundInd.sub_indicators || [];

                    // Restore sub-indicator selection
                    if (savedInd.sub_indicator_code) {
                        const subInd = foundInd.sub_indicators?.find(
                            s => s.code === savedInd.sub_indicator_code
                        );
                        if (subInd) {
                            restoredSelectedSubs[idx] = {
                                value: subInd.code,
                                label: subInd.name
                            };
                        }
                    }
                }
            });

            setTypeIndicators(restoredTypeIndicators);
            setTypeSubIndicators(restoredTypeSubIndicators);
            setSelectedTypeSubIndicators(restoredSelectedSubs);
        }

        // Restore selected locations
        if (reduxMapConfig.Locations) {
            const firstLoc = reduxMapConfig.Locations;
            setLocalSelectedLocations({
                value: firstLoc.lgd_code || firstLoc.value,
                label: firstLoc.lgd_name || firstLoc.label,
                geo_level: firstLoc.geo_level
            });
        }

        setIsRestored(true);
    }, [reduxMapConfig, indicators, isRestored]);

    // Filter indicators that are no longer available in the current workspace
    useEffect(() => {
        if (!indicators || indicators.length === 0) return;

        const availableCodes = new Set(indicators.map(i => i.indicator_code));

        // --- Color Indicator ---
        if (colorIndicator && !availableCodes.has(colorIndicator.value)) {
            setColorIndicator(null);
            setSelectedColorSubIndicator(null);
        }

        // --- Pattern Indicator ---
        if (patternIndicator && !availableCodes.has(patternIndicator.value)) {
            setPatternIndicator(null);
            setSelectedPatternSubIndicator(null);
        }

        // --- Type Indicators (array-based) ---
        if (Array.isArray(typeIndicators) && typeIndicators.length > 0) {
            const filteredTypeIndicators = typeIndicators.filter(ind => {
                if (!ind?.value) return true;
                return availableCodes.has(ind.value);
            });

            if (filteredTypeIndicators.length !== typeIndicators.length) {
                const newTypeSubIndicators = {};
                const newSelectedSubs = {};
                filteredTypeIndicators.forEach((ind, idx) => {
                    const matched = indicators.find(f => f.indicator_code === ind.value);
                    newTypeSubIndicators[idx] = matched?.sub_indicators || [];
                    if (selectedTypeSubIndicators[idx]) {
                        const isStillValid = matched?.sub_indicators?.some(
                            s => s.code === selectedTypeSubIndicators[idx].value
                        );
                        if (isStillValid) newSelectedSubs[idx] = selectedTypeSubIndicators[idx];
                    }
                });

                setTypeIndicators(filteredTypeIndicators);
                setTypeSubIndicators(newTypeSubIndicators);
                setSelectedTypeSubIndicators(newSelectedSubs);
            }
        }
    }, [indicators, colorIndicator, patternIndicator, typeIndicators, selectedTypeSubIndicators]);

    // Handle color indicator change
    const handleColorIndicator = (selected) => {
        setColorIndicator(selected);
        if (selected) {
            const ind = indicators.find(f => f.indicator_code === selected.value);
            setColorSubIndicators(ind?.sub_indicators || []);
            setSelectedColorSubIndicator(null);
        } else {
            setColorSubIndicators([]);
            setSelectedColorSubIndicator(null);
        }
    };

    // Handle pattern indicator change
    const handlePatternIndicator = (selected) => {
        setPatternIndicator(selected);
        if (selected) {
            const ind = indicators.find(f => f.indicator_code === selected.value);
            setPatternSubIndicators(ind?.sub_indicators || []);
            setSelectedPatternSubIndicator(null);
        } else {
            setPatternSubIndicators([]);
            setSelectedPatternSubIndicator(null);
        }
    };

    // Handle chart type change
    const handleChangeChartType = (e) => {
        const value = e.target.value;
        setChartType(value);

        if (value === "pie" || value === "bubble") {
            setTypeIndicators([]);
            setTypeSubIndicators({});
            setSelectedTypeSubIndicators({});
        } else {
            setTypeIndicators([]);
            setTypeSubIndicators({});
            setSelectedTypeSubIndicators({});
        }

        setBool(true);
    };

    // Handle reset
    const handleReset = () => {
        setLocalSelectedLocations([]);
        setChartType("bubble");
        setBool(true);
        setColorIndicator(null);
        setColorSubIndicators([]);
        setPatternIndicator(null);
        setPatternSubIndicators([]);
        setSelectedColorSubIndicator(null);
        setSelectedPatternSubIndicator(null);
        setTypeIndicators([]);
        setTypeSubIndicators([]);
        setSelectedTypeSubIndicators({});
    };

    // Map Submit Button Enable/Disable Logic
    useEffect(() => {
        const isIndicatorSelected =
            Boolean(patternIndicator) ||
            Boolean(colorIndicator) ||
            (typeIndicators && typeIndicators.some(ind => ind));
        const isLocationSelected = (Array.isArray(localSelectedLocations) && localSelectedLocations.length > 0) || Boolean(localSelectedLocations);

        setIsMapSubmitDisabled(!(isIndicatorSelected && isLocationSelected));
    }, [patternIndicator, colorIndicator, typeIndicators, localSelectedLocations]);

    // ✅ Submit functionality - Prepare both formattedPayload AND reduxPayload
    const handleSubmit = (e) => {
        e.preventDefault();

        locations = [{
            geo_level: 1,
            lgd_name: "India (National)",
            lgd_code: 1000
        }, ...locations];

        const selectedFeatures = locations.filter((f) =>
            Array.isArray(localSelectedLocations)
                ? localSelectedLocations.some(
                    (loc) => loc.value === f.properties?.code || loc.lgd_code || loc.code
                )
                : localSelectedLocations?.value === f.properties?.code || f.lgd_code || f.code
        );

        // ✅ KEEP THE ORIGINAL formattedPayload (for map visualization)
        const formattedPayload = {
            chartType,
            selectedVisualizations: { value: "map", label: "Map" },
            colorIndicator: {
                code: colorIndicator?.value,
                label: colorIndicator?.label,
                subIndicator: selectedColorSubIndicator
                    ? {
                        code: selectedColorSubIndicator.value,
                        label: selectedColorSubIndicator.label,
                    }
                    : {},
            },
            patternIndicator: {
                code: patternIndicator?.value,
                label: patternIndicator?.label,
                subIndicator: selectedPatternSubIndicator
                    ? {
                        code: selectedPatternSubIndicator.value,
                        label: selectedPatternSubIndicator.label,
                    }
                    : {},
            },
            indicators: typeIndicators.map((ind, idx) => ({
                code: ind?.value,
                label: ind?.label,
                subIndicator: selectedTypeSubIndicators[idx]
                    ? {
                        code: selectedTypeSubIndicators[idx].value,
                        label: selectedTypeSubIndicators[idx].label,
                    }
                    : {},
            })),
            selectedLocations: selectedFeatures.length > 0
                ? selectedFeatures.map(loc => ({
                    geo_level: loc.properties?.geo_level_code || loc.geo_level || loc.geo_level_code,
                    lgd_name: loc.properties?.name || loc.name || loc.lgd_name,
                    lgd_code: loc.properties?.code || loc.id || loc.code,
                }))
                : [{
                    geo_level: 1,
                    lgd_name: "India (National)",
                    lgd_code: 1000
                }],
                selectedlocation:localSelectedLocations,
            indicator_info: indicators.reduce((acc, cur) => {
                acc[cur.indicator_code] = cur;
                return acc;
            }, {}),
        };

        // ✅ NEW: Create simplified Redux payload (for restoration)
        const reduxPayload = {
            chartType,
            colorIndicator: colorIndicator?.value || null,
            colorSubIndicator: selectedColorSubIndicator?.value || null,
            patternIndicator: patternIndicator?.value || null,
            patternSubIndicator: selectedPatternSubIndicator?.value || null,
            indicators: typeIndicators
                .filter(ind => ind?.value)
                .map((ind, idx) => ({
                    indicator_code: ind.value,
                    sub_indicator_code: selectedTypeSubIndicators[idx]?.value || null,
                })),
                Locations:localSelectedLocations,
            selectedLocations: selectedFeatures.length > 0
                ? selectedFeatures.map(loc => ({
                    geo_level: loc.properties?.geo_level_code || loc.geo_level || loc.geo_level_code,
                    lgd_name: loc.properties?.name || loc.name || loc.lgd_name,
                    lgd_code: loc.properties?.code || loc.id || loc.code,
                }))
                : [{
                    geo_level: 1,
                    lgd_name: "India (National)",
                    lgd_code: 1000
                }],
            fullIndicatorInfo: indicators.reduce((acc, cur) => {
                acc[cur.indicator_code] = cur;
                return acc;
            }, {}),
        };

        const baseIndicators = typeIndicators.map((ind, idx) => ({
            i_code: ind?.value,
            si_code: chartType === "pie"
                ? "all"
                : (selectedTypeSubIndicators[idx]?.value || "")
        }));

        if (colorIndicator) {
            baseIndicators.push({
                i_code: colorIndicator?.value,
                si_code: selectedColorSubIndicator?.value || "",
            });
        }

        if (patternIndicator) {
            baseIndicators.push({
                i_code: patternIndicator?.value,
                si_code: selectedPatternSubIndicator?.value || ""
            });
        }

        const lgd_geo_level = localSelectedLocations?.geo_level || 1;

        const payload = {
            lgd_code: localSelectedLocations?.value || 1000,
            lgd_geo_level: lgd_geo_level,
            geo_level_code: lgd_geo_level + 1,
            indicators: baseIndicators,
        };

        // ✅ Send both payloads to parent
        onSubmit({ formattedPayload, payload, reduxPayload });
    };

    return (
        <div className="cardbg p-3">
            <Form.Label>Map Configuration</Form.Label>
            <div className="col-md-6">
                <Form.Group className="mb-3">
                    <Form.Label>Location</Form.Label>
                    <Select
                        options={[
                            { label: "India (National)", value: 1000, geo_level: 1 },
                            ...locations.filter(f => (f.properties?.geo_level_code || f.geo_level_code ||f.geo_level )!== 4).map(f => ({
                                value: f.properties?.code || f.id,
                                label: `${f.name || f.properties?.name} (${geoLevelLabel(f.properties?.geo_level_code || f.geo_level || f.geo_level_code || 4)})`,
                                geo_level: f.properties?.geo_level_code || f.geo_level_code || f.geo_level || 4,
                            }))
                        ]}
                        onChange={setLocalSelectedLocations}
                        value={localSelectedLocations}
                        placeholder="Choose Location"
                    />
                </Form.Group>
            </div>

            <Form.Label>Color</Form.Label>
            <div className="row">
                <div className="col-md-6">
                    <Form.Group className="mb-3">
                        <Select
                            options={indicators.filter(f => f.measurement_type !== "Categorical - Nominal - Text").map((f) => ({
                                value: f.indicator_code,
                                label: `${f.indicator_name} (${f.indicator_date})`,
                            }))}
                            onChange={handleColorIndicator}
                            value={colorIndicator}
                            classNamePrefix="select"
                            placeholder="Choose Indicator"
                            noOptionsMessage={noOptionsMessage}
                        />
                    </Form.Group>
                </div>
                {colorIndicator && (
                    <div className="col-md-1 text-end">
                        <Button
                            variant="danger"
                            size="sm"
                            onClick={() => {
                                setColorIndicator(null);
                                setSelectedColorSubIndicator(null);
                            }}
                        >
                            <MdOutlineDeleteOutline />
                        </Button>
                    </div>
                )}
                <div className="col-md-5">
                    {colorIndicator && colorSubIndicators.length > 0 && (
                        <Form.Group className="mb-3">
                            <Select
                                options={colorSubIndicators.map(s => ({
                                    value: s.code,
                                    label: s.name
                                }))}
                                value={selectedColorSubIndicator}
                                onChange={setSelectedColorSubIndicator}
                                placeholder="Choose Sub Indicator"
                                classNamePrefix="select"
                                noOptionsMessage={noOptionsMessage}
                            />
                        </Form.Group>
                    )}
                </div>
            </div>

            <Form.Label>Pattern</Form.Label>
            <div className="row">
                <div className="col-md-6">
                    <Form.Group className="mb-3">
                        <Select
                            options={indicators.filter(f => f.measurement_type !== "Categorical - Nominal - Text").map(f => ({
                                value: f.indicator_code,
                                label: `${f.indicator_name} (${f.indicator_date})`,
                            }))}
                            onChange={handlePatternIndicator}
                            value={patternIndicator}
                            classNamePrefix="select"
                            placeholder="Choose Indicator"
                            noOptionsMessage={noOptionsMessage}
                        />
                    </Form.Group>
                </div>
                {patternIndicator && (
                    <div className="col-md-1 text-end">
                        <Button
                            variant="danger"
                            size="sm"
                            onClick={() => {
                                setPatternIndicator(null);
                                setSelectedPatternSubIndicator(null);
                            }}
                        >
                            <MdOutlineDeleteOutline />
                        </Button>
                    </div>
                )}
                <div className="col-md-5">
                    {patternIndicator && patternSubIndicators.length > 0 && (
                        <Form.Group className="mb-3">
                            <Select
                                options={patternSubIndicators.map(s => ({
                                    value: s.code,
                                    label: s.name
                                }))}
                                value={selectedPatternSubIndicator}
                                onChange={setSelectedPatternSubIndicator}
                                placeholder="Choose Sub Indicator"
                                classNamePrefix="select"
                                noOptionsMessage={noOptionsMessage}
                            />
                        </Form.Group>
                    )}
                </div>
            </div>

            <h5>Type</h5>
            <div className="d-flex mb-3">
                <Form.Check
                    type="radio"
                    label="Bubble"
                    value="bubble"
                    checked={chartType === "bubble"}
                    onChange={handleChangeChartType}
                    className="me-3"
                />
                <Form.Check
                    type="radio"
                    label="Marker"
                    value="marker"
                    checked={chartType === "marker"}
                    onChange={handleChangeChartType}
                    className="me-3"
                />
                <Form.Check
                    type="radio"
                    label="Pie"
                    value="pie"
                    checked={chartType === "pie"}
                    onChange={handleChangeChartType}
                />
            </div>

            {isBool &&
                [...Array(chartType === "pie" ? 1 : 3)].map((_, idx) => {
                    const filteredIndicators =
                        chartType === "pie"
                            ? indicators.filter(
                                (f) => f.measurement_type !== "Categorical - Nominal - Text" && Array.isArray(f.sub_indicators) && f.sub_indicators.length > 0 && f.sub_indicators.some(sub => sub.name && sub.name !== 'NA')
                            )
                            : indicators.filter(f => f.measurement_type !== "Categorical - Nominal - Text");

                    return (
                        <div key={idx} className="row mb-3">
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label>
                                        {chartType.charAt(0).toUpperCase() + chartType.slice(1)} {idx + 1}
                                    </Form.Label>
                                    <Select
                                        options={filteredIndicators.map((f) => ({
                                            value: f.indicator_code,
                                            label: `${f.indicator_name} (${f.indicator_date})`,
                                        }))}
                                        value={typeIndicators[idx] || null}
                                        onChange={(selected) => {
                                            const newIndicators = [...typeIndicators];
                                            newIndicators[idx] = selected;
                                            setTypeIndicators(newIndicators);

                                            if (selected) {
                                                const ind = indicators.find(
                                                    (f) => f.indicator_code === selected.value
                                                );
                                                setTypeSubIndicators((prev) => ({
                                                    ...prev,
                                                    [idx]: ind?.sub_indicators || [],
                                                }));
                                                setSelectedTypeSubIndicators((prev) => ({
                                                    ...prev,
                                                    [idx]: null,
                                                }));
                                            } else {
                                                setTypeSubIndicators((prev) => ({ ...prev, [idx]: [] }));
                                            }
                                        }}
                                        classNamePrefix="select"
                                        placeholder={`Choose ${chartType} ${idx + 1}`}
                                        noOptionsMessage={noOptionsMessage}
                                    />
                                </Form.Group>
                            </div>

                            {chartType !== "pie" && typeIndicators[idx] && typeSubIndicators[idx]?.length > 0 && (
                                <div className="col-md-4">
                                    <Form.Group>
                                        <Form.Label>
                                            {chartType.charAt(0).toUpperCase() + chartType.slice(1)}{" "}
                                            {idx + 1} Sub Indicator
                                        </Form.Label>
                                        <Select
                                            options={typeSubIndicators[idx]?.map((s) => ({
                                                value: s.code,
                                                label: s.name,
                                            }))}
                                            value={selectedTypeSubIndicators[idx] || null}
                                            onChange={(selected) => {
                                                setSelectedTypeSubIndicators((prev) => ({
                                                    ...prev,
                                                    [idx]: selected,
                                                }));
                                            }}
                                            classNamePrefix="select"
                                            placeholder={`Choose ${chartType} ${idx + 1} Sub Indicator`}
                                            noOptionsMessage={noOptionsMessage}
                                        />
                                    </Form.Group>
                                </div>
                            )}
                            <div className="col-md-2 mt-4 ui-mb-3">
                                {typeIndicators[idx] && (
                                    <Button
                                        variant="danger"
                                        size="sm"
                                        onClick={() => {
                                            const newIndicators = [...typeIndicators];
                                            newIndicators.splice(idx, 1);
                                            setTypeIndicators(newIndicators);

                                            const newSubIndicators = { ...typeSubIndicators };
                                            delete newSubIndicators[idx];
                                            setTypeSubIndicators(newSubIndicators);

                                            const newSelectedSubs = { ...selectedTypeSubIndicators };
                                            delete newSelectedSubs[idx];
                                            setSelectedTypeSubIndicators(newSelectedSubs);
                                        }}
                                    >
                                        <MdOutlineDeleteOutline />
                                    </Button>
                                )}
                            </div>
                        </div>
                    );
                })}

            <div className="row mt-2">
                <div className="col-md-8 text-start">
                    <p style={{ fontSize: '0.85em' }}>
                        <i>Note:</i> Indicators added to the workspace are listed in the dropdown list.
                        To include additional indicators, go to the Home page and add the required indicators to the workspace.
                    </p>
                </div>
                <div className="text-end col-md-4">
                    <Button variant="secondary" onClick={handleReset} className="btn-sm">
                        Reset
                    </Button>
                    &nbsp;&nbsp;&nbsp;&nbsp;
                    <Button onClick={handleSubmit} className="btn-sm btn-primary" disabled={isMapSubmitDisabled}>
                        Submit
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default MapVisualization;